import React, { useEffect, useReducer } from 'react'
import { styled } from '@mui/material/styles'
import './style.scss'
import {
	Button,
	Dialog,
	FormControl,
	FormGroup,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
	Chip,
} from '@mui/material'
import { GridSearchIcon } from '@mui/x-data-grid'
import { createLibraryData, getLibraryData } from '../../services/library'
import { isMobileScreen, modifyProperty } from '../../utils'
import {
	ALERT,
	LIBRARY_CATEGORY,
	LIBRARY_LANGUAGE,
	LIBRARY_MEDIA_TYPE,
	SADHNA_GROUP,
} from '../../constants'
import { CheckCircleRounded, Visibility, Add } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import store from '../../store'
import { showAlert } from '../../actions'
import { CustomDataGrid } from '../../components/CustomTable/CustomDataGrid'

const PREFIX = 'LibraryContainer'

const classes = {
	tableHeader: `${PREFIX}-tableHeader`,
	modal: `${PREFIX}-modal`,
	modalRoot: `${PREFIX}-modalRoot`,
	paper: `${PREFIX}-paper`,
}

const Root = styled('div')({
	[`& .${classes.tableHeader}`]: {
		padding: '20px',
		marginBottom: '20px',
	},
	[`& .${classes.modal}`]: {
		display: 'flex',
		// padding: theme.spacing(1),
		alignItems: 'center',
		justifyContent: 'center',
	},
	[`& .${classes.modalRoot}`]: {
		// height: 300,
		position: 'absolute',
		flexGrow: 1,
		minWidth: 300,
		transform: 'translateZ(0)',
		// The position fixed scoping doesn't work in IE 11.
		// Disable this demo to preserve the others.
		'@media all and (-ms-high-contrast: none)': {
			display: 'none',
		},
	},
})

const styles = {
	paper: {
		width: 400,
		backgroundColor: 'white',
		// border: '2px solid #000',
		borderRadius: '8px',
		// boxShadow: theme.shadows[5],
		padding: '40px',
		lineHeight: '1.44',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
}

const initialState = {
	data: [],
	form: {
		lang: '',
		mediaType: '',
		category: '',
		sadhnaGroup: [],
		url: '',
		title: '',
	},
	modal: false,
	filters: {
		pageSize: 10,
		page: 0,
		total_results: 0,
		total_pages: 0,
		language: '',
		mediaType: '',
		search: '',
		sadhnaGroup: [],
	},
}

const MONTH_LABELS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

const formatDate = (dateValue) => {
	if (!dateValue) return '—'
	const date = new Date(dateValue)
	if (Number.isNaN(date.getTime())) {
		return '—'
	}
	return `${date.getDate()} ${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`
}

function LibraryContainer(props) {
	const [{ data, modal, form, loading, filters }, setState] = useReducer(
		(prev, data) => ({
			...prev,
			...data,
			filters: { ...prev.filters, ...data.filters },
			form: { ...prev.form, ...data.form },
		}),
		initialState
	)

	const isMobile = isMobileScreen()
	const params = useParams()
	const { libraryType } = params

	const fetchData = () => {
		const body = {
			id: '',
			category: LIBRARY_CATEGORY[libraryType].id,
			status: '',
			mediaType: filters.mediaType,
			sadhnaGroup: Array.isArray(filters.sadhnaGroup)
				? filters.sadhnaGroup?.join(',')
				: '',
			title: filters.search,
			lang: filters.language,
			page: filters.page + 1,
			per_page: filters.pageSize,
		}
		setState({
			loading: true,
		})
		getLibraryData(body).then((res) => {
			const { data, total_pages, total_results, current_page } =
				res?.data || {}
			setState({
				data,
				loading: false,
				filters: {
					total_results,
					total_pages,
				},
			})
		})
	}

	useEffect(() => {
		fetchData()
	}, [
		libraryType,
		filters.mediaType,
		filters.language,
		filters.search,
		filters.sadhnaGroup,
		filters.page,
		filters.pageSize,
	])

	const rowWidthConfig = {
		title: { default: { flex: 0.3 }, mobile: { width: 180 } },
		language: { default: { flex: 0.12 }, mobile: { width: 120 } },
		mediaType: { default: { flex: 0.14 }, mobile: { width: 140 } },
		sadhnaGroup: { default: { flex: 0.24 }, mobile: { width: 200 } },
		createdAt: { default: { flex: 0.14 }, mobile: { width: 160 } },
		action: { default: { flex: 0.1 }, mobile: { width: 120 } },
	}

	const headCells = [
		{
			field: 'title',
			numeric: false,
			headerName: 'Title',
			sortable: false,
		},
		{
			field: 'language',
			numeric: false,
			headerName: 'Language',
			sortable: false,
			renderCell: (params) => (
				<Chip
					label={params.row.language || 'N/A'}
					size="small"
					color="primary"
					variant="outlined"
				/>
			),
		},
		{
			field: 'mediaType',
			numeric: false,
			headerName: 'Media Type',
			sortable: false,
			renderCell: (params) => (
				<Chip
					label={params.row.mediaType || 'N/A'}
					size="small"
					color="secondary"
					variant="outlined"
				/>
			),
		},
		{
			field: 'sadhnaGroup',
			numeric: false,
			headerName: 'Sadhna Group',
			sortable: false,
			renderCell: (params) => {
				const values = Array.isArray(params.row.sadhnaGroup)
					? params.row.sadhnaGroup
					: []
				return values.length ? (
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{values.map((group) => (
							<Chip key={group} label={group} size="small" />
						))}
					</Stack>
				) : (
					<span>—</span>
				)
			},
		},
		{
			field: 'createdAt',
			numeric: false,
			headerName: 'Updated On',
			sortable: false,
		},
		{
			field: 'action',
			headerName: 'Action',
			renderCell: (params) => {
				return (
					<IconButton
						onClick={() => {
							window.open(params.row.url, '_blank')
						}}
						aria-label="View"
						color="primary"
						title="View"
						size="large"
					>
						<Visibility />
					</IconButton>
				)
			},
		},
	]

	const onFilterChange = (name, value) => {
		setState({
			filters: {
				[name]: value,
				page: 1,
			},
		})
	}

	const onPageSizeChange = ({ page, pageSize }) => {
		setState({
			filters: {
				page,
				pageSize,
			},
		})
	}

	const handleInput = (name, value) => {
		setState({
			form: {
				...form,
				[name]: value,
			},
		})
	}

	const createAction = () => {
		const validateForm = (form, libraryType) => {
			const { mediaType, title, lang, url, sadhnaGroup } = form
			const messages = {
				category: 'Category is invalid or missing.',
				mediaType: 'Media type is required.',
				title: 'Title is required.',
				lang: 'Language is required.',
				url: 'URL is empty or invalid.',
				sadhnaGroup: 'Sadhna group cannot be empty if selected.',
			}

			const urlPattern =
				/^(https?:\/\/)?([\w\-])+(\.[\w\-]+)+([\w\-,@?^=%&:/~+#]*[\w\-@?^=%&/~+#])?$/

			// Define validation conditions
			const validations = [
				[!LIBRARY_CATEGORY[libraryType]?.id, messages.category],
				[!mediaType, messages.mediaType],
				[!title, messages.title],
				[!lang, messages.lang],
				[!url || !urlPattern.test(url), messages.url], // URL validation
				[
					Array.isArray(sadhnaGroup) && sadhnaGroup.length === 0,
					messages.sadhnaGroup,
				],
			]

			// Iterate and dispatch alert for first failed validation
			for (const [condition, message] of validations) {
				if (condition) {
					store.dispatch(
						showAlert({
							message,
							severity: ALERT.ERROR,
							duration: 5000,
						})
					)
					return false
				}
			}

			return true
		}

		// Usage

		const body = {
			category: LIBRARY_CATEGORY[libraryType]?.id,
			mediaType: form.mediaType,
			sadhnaGroup: Array.isArray(form.sadhnaGroup)
				? form.sadhnaGroup.join(',')
				: '',
			title: form.title,
			lang: form.lang,
			url: form.url,
		}

		if (validateForm(form, libraryType)) {
			// Proceed with submission logic
			setState({
				loading: true,
			})
			createLibraryData(body).then((res) => {
				fetchData()
				setState({
					modal: false,
					loading: false,
					form: {
						category: '',
						mediaType: '',
						sadhnaGroup: [],
						title: '',
						lang: '',
						url: '',
					},
				})
			})
		}
	}

	return (
		<Root className="library-container">
			<Stack spacing={3}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					alignItems={{ xs: 'flex-start', md: 'center' }}
					justifyContent="space-between"
					spacing={1.5}
				>
					<div>
						<Typography variant="h4" fontWeight={600} gutterBottom>
							Library
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{LIBRARY_CATEGORY[libraryType]?.text || 'All'} •{' '}
							{filters.total_results || 0} items
						</Typography>
					</div>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => setState({ modal: true })}
						size="large"
					>
						Create Content
					</Button>
				</Stack>

				<Paper elevation={0} className="p-3">
					<Stack
						direction={'row'}
						spacing={2}
						alignItems={'center'}
						flexWrap="wrap"
					>
						<TextField
							id="input-with-icon-textfield"
							label="Search"
							variant='standard'
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<GridSearchIcon />
									</InputAdornment>
								),
							}}
							name="search"
							onChange={(e) =>
								onFilterChange(e.target.name, e.target.value)
							}
							autoComplete="off"
							value={filters.search}
							sx={{ minWidth: 260, flex: '0 1 auto' }}
						/>

						<FormControl variant='standard' sx={{ minWidth: 200 }}>
							<InputLabel id="library-language">
								Language
							</InputLabel>
							<Select
								labelId="library-language"
								value={filters.language}
								onChange={(e) =>
									onFilterChange(
										e.target.name,
										e.target.value
									)
								}
								displayEmpty
								label="Language"
								name="language"
								size="small"
								MenuProps={{ PaperProps: { style: { maxHeight: 48 * 6 } } }}
								sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 2 }}
								renderValue={(val) => {
									if (!val) return <span style={{ color: '#888' }}>All Language</span>
									return LIBRARY_LANGUAGE.getById(Number(val))?.text || LIBRARY_LANGUAGE.getById(val)?.text || 'Unknown'
								}}
							>
								<MenuItem value="">All Language</MenuItem>
								{LIBRARY_LANGUAGE.getAll().map((lang) => (
									<MenuItem value={lang.id} key={lang.id}>
										{lang.text}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl variant='standard' sx={{ minWidth: 220 }}>
							<InputLabel id="library-media-type">
								Media Type
							</InputLabel>
							<Select
								labelId="library-media-type"
								value={filters.mediaType}
								onChange={(e) =>
									onFilterChange(
										e.target.name,
										e.target.value
									)
								}
								displayEmpty
								label="Media Type"
								name="mediaType"
								size="small"
								MenuProps={{ PaperProps: { style: { maxHeight: 48 * 6 } } }}
								sx={{ minWidth: 220, bgcolor: 'white', borderRadius: 2 }}
								renderValue={(val) => {
									if (!val) return <span style={{ color: '#888' }}>All Media Types</span>
									return LIBRARY_MEDIA_TYPE.getById(Number(val))?.text || LIBRARY_MEDIA_TYPE.getById(val)?.text || 'Unknown'
								}}
							>
								<MenuItem value="">All Media Types</MenuItem>
								{LIBRARY_MEDIA_TYPE.getAll(libraryType).map((el) => (
									<MenuItem value={el.id} key={el.id}>
										{el.text}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl variant='standard' sx={{ minWidth: 260 }}>
							<InputLabel id="library-sadhna-group">
								Sadhna Group
							</InputLabel>
							<Select
								labelId="library-sadhna-group"
								multiple
								value={filters.sadhnaGroup}
								onChange={(e) =>
									onFilterChange(
										e.target.name,
										e.target.value
									)
								}
								displayEmpty
								label="Sadhna Group"
								name="sadhnaGroup"
								size="small"
								MenuProps={{ PaperProps: { style: { maxHeight: 48 * 6 } } }}
								sx={{ minWidth: 260, bgcolor: 'white', borderRadius: 2 }}
								renderValue={(selected) => {
									if (!selected || (Array.isArray(selected) && selected.length === 0)) {
										return <span style={{ color: '#888' }}>All Sadhna Groups</span>
									}
									const values = Array.isArray(selected) ? selected : String(selected).split(',')
									return (
										<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
											{values.map((v) => {
												const entry = SADHNA_GROUP.getById(Number(v)) || SADHNA_GROUP.getById(v)
												return <Chip key={String(v)} size="small" label={entry?.text || v} />
											})}
										</Stack>
									)
								}}
							>
								<MenuItem value="">All Sadhna Groups</MenuItem>
								{SADHNA_GROUP.getAll().map((el) => (
									<MenuItem value={el.id} key={el.id}>
										{el.text}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Paper>

				<Paper elevation={0} className="p-3">
					<CustomDataGrid
						rows={modifyProperty(data, '_id', 'id').map((el) => {
							const mediaTypeEntry =
								LIBRARY_MEDIA_TYPE.getById(Number(el.mediaType)) ||
								LIBRARY_MEDIA_TYPE.getById(el.mediaType)

							const languageEntry =
								LIBRARY_LANGUAGE.getById(Number(el.lang)) ||
								LIBRARY_LANGUAGE.getById(el.lang)

							const rawSadhnaGroup = el.sadhnaGroup
							const sadhnaGroupValues = []

							const toGroupText = (value) => {
								const entry =
									SADHNA_GROUP.getById(Number(value)) ||
									SADHNA_GROUP.getById(value)
								return entry?.text || `${value}`
							}

							if (Array.isArray(rawSadhnaGroup)) {
								sadhnaGroupValues.push(
									...rawSadhnaGroup
										.map((value) => value)
										.filter((value) => value !== '' && value !== null)
										.map(toGroupText)
								)
							} else if (typeof rawSadhnaGroup === 'string') {
								sadhnaGroupValues.push(
									...rawSadhnaGroup
										.split(',')
										.map((value) => value.trim())
										.filter(Boolean)
										.map(toGroupText)
								)
							} else if (rawSadhnaGroup != null) {
								sadhnaGroupValues.push(toGroupText(rawSadhnaGroup))
							}

							return {
								...el,
								mediaType: mediaTypeEntry?.text || el.mediaType || 'N/A',
								language: languageEntry?.text || el.lang || 'N/A',
								sadhnaGroup: sadhnaGroupValues,
								createdAt: formatDate(el.updatedAt || el.updated_at || el.createdAt || el.created_at),
							}
						})}
						columns={headCells.map((col) => ({
							...col,
							sortable: true,
							disableClickEventBubbling: true,
							...(isMobile
								? rowWidthConfig[col.field].mobile ||
								rowWidthConfig[col.field].default
								: rowWidthConfig[col.field].default),
						}))}
						rowCount={filters.total_results}
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: filters.pageSize,
								},
							},
						}}
						disableColumnFilter
						loading={loading}
						paginationModel={filters}
						onPaginationModelChange={(page) =>
							onPageSizeChange({
								pageSize: page.pageSize,
								page:
									page.pageSize != filters.pageSize
										? 0
										: page.page,
							})
						}
						sx={{
							'& .MuiDataGrid-columnHeaders': {
								backgroundColor: '#f7f7f7',
								fontWeight: 600,
							},
							'& .MuiDataGrid-row:nth-of-type(odd)': {
								backgroundColor: '#fafafa',
							},
							'& .MuiChip-root': {
								textTransform: 'capitalize',
							},
						}}
					/>
				</Paper>
			</Stack>
			<Dialog
				open={!!modal}
				onClose={() => setState({ modal: null })}
				className={classes.modal}
			>
				<div style={styles.paper}>
					<h1>Create Content</h1>
					<FormGroup row={1} sx={{ width: '100%' }}>
						<TextField
							className="m-top-16"
							label="Title"
							variant="outlined"
							value={form.title}
							required
							type="string"
							name="title"
							onChange={(e) =>
								handleInput(e.target.name, e.target.value)
							}
							fullWidth
						/>
						<TextField
							className="m-top-16"
							label="URL"
							variant="outlined"
							required
							value={form.url}
							type="string"
							name="url"
							onChange={(e) =>
								handleInput(e.target.name, e.target.value)
							}
							fullWidth
						/>
						<FormControl
							variant="outlined"
							fullWidth
							className="m-top-16"
						>
							<InputLabel id="library-language">
								Language
							</InputLabel>
							<Select
								labelId="library-language"
								id="filter-dropdown"
								value={form.lang}
								required
								onChange={(e) =>
									handleInput(e.target.name, e.target.value)
								}
								label="Language"
								name="lang"
							>
								<MenuItem value={''}>Select</MenuItem>
								{LIBRARY_LANGUAGE.getAll().map((lang) => (
									<MenuItem value={lang.id}>
										{lang.text}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl
							variant="outlined"
							fullWidth
							className="m-top-16"
						>
							<InputLabel id="input-label">Media Type</InputLabel>
							<Select
								labelId="input-label"
								id="filter-dropdown"
								value={form.mediaType}
								required
								onChange={(e) =>
									handleInput(e.target.name, e.target.value)
								}
								label="Media Type"
								name="mediaType"
							>
								<MenuItem value={''}>Select</MenuItem>
								{LIBRARY_MEDIA_TYPE.getAll(libraryType).map(
									(el) => (
										<MenuItem value={el.id}>
											{el.text}
										</MenuItem>
									)
								)}
							</Select>
						</FormControl>

						<FormControl
							variant="outlined"
							fullWidth
							className="m-top-16"
						>
							<InputLabel id="input-label">
								Sadhna Group
							</InputLabel>
							<Select
								multiple
								labelId="input-label"
								id="filter-dropdown"
								value={form.sadhnaGroup}
								required
								onChange={(e) =>
									handleInput(e.target.name, e.target.value)
								}
								label="Sadhna Group"
								name="sadhnaGroup"
							>
								<MenuItem value={''}>
									All Sadhna Groups
								</MenuItem>
								{SADHNA_GROUP.getAll().map((el) => (
									<MenuItem value={el.id}>{el.text}</MenuItem>
								))}
							</Select>
						</FormControl>
					</FormGroup>
					<div>
						<Button
							className="m-top-16"
							variant="contained"
							color="primary"
							startIcon={<CheckCircleRounded />}
							onClick={createAction}
							aria-label="Accept"
							style={{ marginRight: '12px' }}
						>
							Create
						</Button>
					</div>
				</div>
			</Dialog>
		</Root>
	)
}

export default LibraryContainer
